import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from '../Toast';
import { MessageSquare, Send, Loader2, ChevronDown, ChevronUp, Lock, Globe } from 'lucide-react';
import { crmApi } from '../../services/crmApi';

interface Message {
  id: number;
  text: string;
  source: 'STAFF' | 'CLIENT' | 'STAFF_NOTE';
  visible: boolean;
  created_at: string;
}

interface ClientChatProps {
  applicationId: number;
  clientName?: string;
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'teraz';
  if (mins < 60) return `${mins} min temu`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h temu`;
  const days = Math.floor(hrs / 24);
  return `${days}d temu`;
}

export const ClientChat: React.FC<ClientChatProps> = ({ applicationId, clientName }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [isInternal, setIsInternal] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      const data = await crmApi.getMessages(applicationId);
      setMessages(data.messages || []);
      setUnread(data.unreadCount || 0);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [applicationId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (expanded && endRef.current) {
      endRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length, expanded]);

  useEffect(() => {
    if (expanded && unread > 0) {
      crmApi.markMessagesRead(applicationId).then(() => setUnread(0)).catch(() => {});
    }
  }, [expanded, unread, applicationId]);

  // Auto-refresh every 30s when expanded
  useEffect(() => {
    if (!expanded) return;
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [expanded, load]);

  const handleSend = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      await crmApi.sendMessage(applicationId, text.trim(), isInternal);
      setText('');
      await load();
    } catch {
      toast.error('Błąd wysyłania wiadomości');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {/* Header — toggle */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <MessageSquare size={18} className="text-indigo-600" />
          <span className="font-bold text-sm">
            Czat z klientem
            {clientName && <span className="font-normal text-gray-500 ml-1">({clientName})</span>}
          </span>
          {unread > 0 && (
            <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full animate-pulse">
              {unread}
            </span>
          )}
          {!loading && messages.length > 0 && (
            <span className="text-[10px] text-gray-400">({messages.length})</span>
          )}
        </div>
        {expanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
      </button>

      {expanded && (
        <div className="border-t border-gray-100">
          {/* Messages */}
          <div className="max-h-72 overflow-y-auto p-3 space-y-2 bg-gray-50">
            {loading ? (
              <div className="flex justify-center py-6">
                <Loader2 size={20} className="animate-spin text-gray-400" />
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center py-6 text-gray-400 text-xs">
                <MessageSquare size={24} className="mx-auto mb-2 opacity-40" />
                Brak wiadomości. Napisz pierwsza!
              </div>
            ) : (
              messages.map((msg) => {
                const isStaff = msg.source === 'STAFF';
                const isNote = msg.source === 'STAFF_NOTE';
                const isRight = isStaff || isNote;
                return (
                  <div key={msg.id} className={`flex ${isRight ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[85%] rounded-xl px-3 py-2 ${
                        isNote
                          ? 'bg-amber-50 border border-amber-200 text-amber-900 rounded-br-sm'
                          : isStaff
                            ? 'bg-indigo-600 text-white rounded-br-sm'
                            : 'bg-white border border-gray-200 text-gray-800 rounded-bl-sm'
                      }`}
                    >
                      {isNote && (
                        <div className="flex items-center gap-1 mb-0.5">
                          <Lock size={10} className="text-amber-500" />
                          <span className="text-[9px] font-semibold text-amber-500 uppercase">Notatka wewnetrzna</span>
                        </div>
                      )}
                      <p className="text-xs whitespace-pre-wrap">{msg.text}</p>
                      <p className={`text-[10px] mt-1 ${isNote ? 'text-amber-400' : isStaff ? 'text-indigo-200' : 'text-gray-400'}`}>
                        {isRight ? 'Ty' : clientName || 'Klient'} &middot; {timeAgo(msg.created_at)}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={endRef} />
          </div>

          {/* Compose */}
          <div className="p-3 border-t border-gray-100 flex gap-2 items-end">
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={2}
              placeholder="Napisz wiadomosc..."
              className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 resize-none"
            />
            <button
              onClick={handleSend}
              disabled={!text.trim() || sending}
              className="shrink-0 w-9 h-9 bg-indigo-600 text-white rounded-lg flex items-center justify-center hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </button>
          </div>
          <div className="px-3 pb-2 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setIsInternal(!isInternal)}
              className={`flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-full transition-colors ${
                isInternal
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-indigo-50 text-indigo-600'
              }`}
            >
              {isInternal ? <><Lock size={10} /> Notatka wewnetrzna</> : <><Globe size={10} /> Wiadomosc do klienta</>}
            </button>
            <span className="text-[10px] text-gray-400">
              {isInternal ? 'Tylko dla zespolu' : 'Klient otrzyma email'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
